import React, { useEffect, useRef, useState } from 'react';
import SelectField from '../components/SelectField';
import { FaSave, FaPen, FaPlus, FaTimes, FaTrash, FaCheck, FaExclamationTriangle, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import LoadingState from '../components/LoadingState';
import { useNavigate, useParams } from 'react-router-dom';
import FileUpload from "../components/FileUpload";
import { fetchTemplate, createTemplateApi, listCampaignsApi, getAllTemplates, presign, addKbFileToTemplate, deleteKbFileFromTemplate } from '../api/api';
import axios from 'axios';
import ConfirmationModal from '../components/ConfirmationModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import useUnsavedChangesGuard from '../hooks/useUnsavedChangesGuard';
import {
  ASSESSMENT_TYPE,
  ASSESSMENT_TYPE_OPTIONS,
  ASSESSMENT_TYPE_SHORT_LABELS,
  SCORING_SYSTEM,
  asComplianceQuestion,
  isWeightedType,
  scoringSystemOf,
  looksLikeScoringInstruction,
  supportsApplicability,
  weightingGridClass,
} from '../common/assessment';
import { devLog } from '../common/devLog';
import { moveItem, sortQuestions, withClientKey, withClientKeys, withSortOrder } from '../common/questionOrder';

const SCORING_SYSTEM_OPTIONS = [
  {
    value: SCORING_SYSTEM.YES_NO,
    label: 'Yes/No Scoring (v2.0)',
    description: 'Questions get full weight if "Yes", zero if "No"',
  },
  {
    value: SCORING_SYSTEM.LEGACY,
    label: 'Numeric Scoring (v1.0 - Legacy)',
    description: 'Questions scored 0-100',
  },
  {
    value: SCORING_SYSTEM.COMPLIANCE,
    label: 'Compliance-only',
    description: 'Every question is a pass/fail compliance check - no QA score, calls pass or fail on compliance',
  },
];

const LANGUAGE_OPTIONS = [
  { value: 'en-ZA', label: 'en-ZA' },
  { value: 'en-US', label: 'en-US' },
  { value: 'en-GB', label: 'en-GB' },
  { value: 'zu-ZA', label: 'zu-ZA (Zulu - South Africa)' },
  { value: 'auto-detect', label: 'Auto Detect' },
];

// Header chip colors per assessment type
const TYPE_BADGE_CLASSES = {
  [ASSESSMENT_TYPE.QUALITY]: 'bg-cloudza-50 text-cloudza-700 border-cloudza-100',
  [ASSESSMENT_TYPE.COMPLIANCE]: 'bg-amber-50 text-amber-700 border-amber-200',
  [ASSESSMENT_TYPE.CONDUCT]: 'bg-red-50 text-red-700 border-red-200',
};

const emptyQuestion = {
  questionName: '',
  description: '',
  type: ASSESSMENT_TYPE.QUALITY,
  weight: '',
  expected: '',
  // v2.0 fields
  section: '',
  assessment: '',
  responseType: 'yes_no',
  qaWeighting: '',
  complianceWeighting: '',
  isCriticalFail: false,
  success: 'yes',  // Default to 'yes' - positive answer awards points
  sortOrder: 999,
  // Conduct-only: pre-check steps the assessor evaluates before the question itself
  isConditional: false,
  applicabilityConditions: [],
  priority: 'medium'
};

const TemplateEditor = ({ setAlert }) => {
  const navigate = useNavigate();
  const { name } = useParams();
  const [templates, setTemplates] = useState([])
  const [templateName, setTemplateName] = useState('');
  const [campaign, setCampaign] = useState('');
  const [questions, setQuestions] = useState(() => withClientKeys([ { ...emptyQuestion } ]));
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveConfirm, setSaveConfirm] = useState(false);
  const [dirty, setDirty] = useState(false);
  const blocker = useUnsavedChangesGuard(dirty);
  const [inputError, setInputError] = useState([false]);
  // A failed save must land the user on the error, not leave them wherever
  // the confirm dialog was — the banner sits near the save row and can be
  // far off-screen on long forms.
  const errorRef = useRef(null);
  useEffect(() => {
    if (inputError[0]) {
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [inputError]);
  const [saveCurrent, setSaveCurrent] = useState(false);
  const [scoringSystem, setScoringSystem] = useState(SCORING_SYSTEM.YES_NO); // v2.0 by default
  // Compliance-only answers yes/no too - it is the yes/no system with every question Compliance.
  const yesNoScoring = scoringSystem !== SCORING_SYSTEM.LEGACY;
  const complianceOnly = scoringSystem === SCORING_SYSTEM.COMPLIANCE;
  // Switching to Compliance-only converts non-Compliance questions, so it is confirmed first.
  const [confirmComplianceOnly, setConfirmComplianceOnly] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('en-ZA');
  const [upload, setUpload] = useState([]);
  const [kbFiles, setKbFiles] = useState([]);
  const [deleteKbInfo, setDeleteKbInfo] = useState({ open: false, fileId: null });

  const handleFileAdd = ({ detail }) => {
    const newFiles = detail.value;
    setUpload(newFiles);
  };

  useEffect(() => {
    const loadCampaigns = async () => {
      try {
        const res = await listCampaignsApi();
        setCampaigns(res.campaigns || []);
        const res2 = await getAllTemplates();
        setTemplates(res2.templates || [])
      } catch (err) {
        console.error('Failed to load campaigns', err);
        setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to load campaigns.' });
      }
    };
    loadCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    if (!name) {
      setLoading(false);
      setDirty(false);
      return;
    }
    const load = async () => {
      try {
        const res = await fetchTemplate(name);
        setTemplateName(res.templateName);
        // The API already returns them in order; sorting again keeps the editor right against an
        // older API that does not.
        const loadedQuestions = sortQuestions(res.questions || []);

        // Migrate old 'weight' field to 'qaWeighting' if needed
        const migratedQuestions = withClientKeys(loadedQuestions.map(q => ({
          ...q,
          qaWeighting: q.qaWeighting || q.weight || '',
        })));

        setQuestions(migratedQuestions);

        // Load language if available
        if (res.language) {
          setSelectedLanguage(res.language);
        }

        // Load KB files if available
        if (res.kbFiles) {
          setKbFiles(res.kbFiles);
        }

        // Compliance-only is stored on the scorecard; Yes/No and Legacy are detected from the
        // questions, as they always were.
        setScoringSystem(scoringSystemOf({ ...res, questions: loadedQuestions }));
      } catch (err) {
        console.error('Failed to fetch scorecard', err);
        setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to load scorecard.' });
      } finally {
        setLoading(false);
        setDirty(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  const nonComplianceCount = questions.filter(q => q.type !== ASSESSMENT_TYPE.COMPLIANCE).length;

  const applyScoringSystem = (next) => {
    setScoringSystem(next);
    setDirty(true);
    setQuestions(q => q.map(question =>
      next === SCORING_SYSTEM.COMPLIANCE
        ? asComplianceQuestion(question)
        : { ...question, responseType: next === SCORING_SYSTEM.LEGACY ? 'scored' : 'yes_no' }
    ));
  };

  const chooseScoringSystem = (next) => {
    if (next === scoringSystem) return;
    if (next === SCORING_SYSTEM.COMPLIANCE && nonComplianceCount > 0) {
      setConfirmComplianceOnly(true);
      return;
    }
    applyScoringSystem(next);
  };

  const addQuestion = () => {
    const newQuestion = complianceOnly
      ? asComplianceQuestion(emptyQuestion)
      : { ...emptyQuestion, responseType: yesNoScoring ? 'yes_no' : 'scored' };
    setQuestions(q => [...q, withClientKey(newQuestion)]);
    setDirty(true);
  };
  const updateQuestion = (index, key, value) => { setQuestions(q => q.map((item,i)=> i===index ? { ...item, [key]: value } : item)); setDirty(true); };
  const removeQuestion = (index) => { setQuestions(q => q.filter((_,i)=>i!==index)); setDirty(true); };
  // The order here is the order saved (sortOrder 1..N), and the order the call page and reports use.
  const moveQuestion = (index, offset) => { setQuestions(q => moveItem(q, index, offset)); setDirty(true); };

  // Applicability pre-check steps, stored as an ordered array of strings on the question. Every
  // step must hold for the question to be assessed at all; the assessor evaluates them in order in
  // a separate pass before the question itself.
  const setSteps = (index, next) => {
    setQuestions(q => q.map((item,i)=> i===index
      ? { ...item, applicabilityConditions: next(item.applicabilityConditions || []) }
      : item));
    setDirty(true);
  };
  const addStep = (index) => setSteps(index, steps => [...steps, '']);
  const updateStep = (index, stepIndex, value) =>
    setSteps(index, steps => steps.map((s,si)=> si===stepIndex ? value : s));
  const removeStep = (index, stepIndex) =>
    setSteps(index, steps => steps.filter((_,si)=> si!==stepIndex));

  // Calculate total QA weighting for yes/no scoring (only Quality questions)
  const calculateTotalQaWeighting = () => {
    if (!yesNoScoring) return null;
    return questions.reduce((sum, q) => {
      // Only Quality questions carry weighting - Compliance and Conduct are pass/fail only
      if (isWeightedType(q.type)) {
        const weight = parseFloat(q.qaWeighting || q.weight || 0);
        return sum + weight;
      }
      return sum;
    }, 0);
  };

  // Calculate total Compliance weighting for yes/no scoring (only Quality questions)
  const calculateTotalComplianceWeighting = () => {
    if (!yesNoScoring) return null;
    return questions.reduce((sum, q) => {
      // Only Quality questions carry weighting - Compliance and Conduct are pass/fail only
      if (isWeightedType(q.type)) {
        const weight = parseFloat(q.complianceWeighting || 0);
        return sum + weight;
      }
      return sum;
    }, 0);
  };

  // A question marked "not always applicable" with no steps would be saved with an empty list,
  // which the server reads as "not gated" - the question would silently be assessed on every call.
  // The server also drops blank steps without telling anyone, so this is the only visible feedback.
  // Returns an error message, or null when the questions are fine.
  const validateApplicabilitySteps = () => {
    const bad = questions.findIndex(q =>
      supportsApplicability(q.type) &&
      q.isConditional &&
      !(q.applicabilityConditions || []).some(step => String(step).trim())
    );
    if (bad === -1) return null;
    return `Question ${bad + 1} is marked "not always applicable" but has no pre-check steps. `
         + `Add at least one step, or untick the box.`;
  };

  // Quality/Compliance weighting only applies to Quality-type questions — a scorecard made up
  // entirely of Compliance questions has nothing to weight, so the 100% checks below don't apply.
  const hasQualityQuestions = () => questions.some(q => q.type === 'Quality');

  // The backend keys each question on questionName, so a blank name fails the save and a
  // duplicate silently overwrites the other question. Returns an error message, or null.
  const validateQuestionNames = () => {
    const seen = new Set();
    for (let i = 0; i < questions.length; i++) {
      const qName = questions[i].questionName;
      if (!qName || !String(qName).trim()) {
        return `Question ${i + 1} has no Question Name. Every question needs a name before saving.`;
      }
      if (seen.has(qName)) {
        return `Duplicate question name '${qName}'. Question names must be unique.`;
      }
      seen.add(qName);
    }
    return null;
  };

  const handleSave = async () => {
    const nameError = validateQuestionNames();
    if (nameError) {
      setInputError([true, nameError]);
      setSaveConfirm(false);
      return;
    }

    const stepError = validateApplicabilitySteps();
    if (stepError) {
      setInputError([true, stepError]);
      setSaveConfirm(false);
      return;
    }

    // Validate QA weighting totals 100 for yes/no scoring
    if (yesNoScoring && hasQualityQuestions()) {
      const qaTotal = calculateTotalQaWeighting();
      if (Math.abs(qaTotal - 100) > 0.01) {  // Allow small floating point differences
        setInputError([true, `QA Weighting must total exactly 100%. Current total: ${qaTotal.toFixed(2)}%`]);
        setSaveConfirm(false);
        return;
      }

      // Validate Compliance weighting totals 100 for yes/no scoring
      const complianceTotal = calculateTotalComplianceWeighting();
      if (Math.abs(complianceTotal - 100) > 0.01) {  // Allow small floating point differences
        setInputError([true, `Compliance Weighting must total exactly 100%. Current total: ${complianceTotal.toFixed(2)}%`]);
        setSaveConfirm(false);
        return;
      }
    }

    if (dirty && name && templateName.trim() === name.trim()) {
      setInputError([true, `Please provide a different version name before saving.`])
      setSaveConfirm(false);
      return;
    }
    for (let i =0; i<= (templates || []).length -1 ; i++) {
      if (templates[i].templateName === templateName) {
        setInputError([true, `Template name '${templates[i].templateName}' already exists.`])
        setSaveConfirm(false);
        return;
      }      
    }

    setSaving(true);
    try {
      const payload = name
        ? {
            isVersionCreation: true,
            newTemplateName: templateName,
            originalTemplateName: name,
            campaign: campaign || undefined,
            language: selectedLanguage,
            scoringSystem,
            questions: withSortOrder(questions),
          }
        : { templateName, campaign: campaign || undefined, language: selectedLanguage, scoringSystem, questions: withSortOrder(questions) };
        devLog("PAYLOAD: ", payload)
      await createTemplateApi(payload);

      // Upload knowledge base files for this template and register in KB management
      if (upload.length !== 0) {
        for (let i = 0; i < upload.length; i++) {
          const filename = upload[i].name;
          const s3Up = await presign(filename, 'scorecard-kb', templateName);
          await axios.put(s3Up.url, upload[i]);
          // Register the file in KB management system linked to this template
          await addKbFileToTemplate(templateName, filename, s3Up.key || filename);
        }
        setUpload([]);
      }

      setAlert?.({ variant: 'success', heading: 'Scorecard Saved', text: 'Scorecard saved successfully.' });
      setDirty(false);
      navigate('/templates');
    } catch (err) {
      console.error('Save failed', err);
      setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to save scorecard.' });
    } finally {
      setSaving(false);
      // Whatever happened, the confirm dialog's job is done — an error must
      // be read on the page, not behind a still-open dialog.
      setSaveConfirm(false);
    }
  };

  // Delete KB file handler
  const handleDeleteKbFile = async () => {
    if (!deleteKbInfo.fileId || !name) return;
    try {
      await deleteKbFileFromTemplate(name, deleteKbInfo.fileId);
      setKbFiles(files => files.filter(f => f !== deleteKbInfo.fileId));
      setAlert?.({ variant: 'success', heading: 'File Deleted', text: 'Knowledge base file deleted successfully.' });
    } catch (err) {
      console.error('Failed to delete KB file', err);
      setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to delete knowledge base file.' });
    } finally {
      setDeleteKbInfo({ open: false, fileId: null });
    }
  };

  const handleSaveCurrent = async () => {
    // Same rules as handleSave - the two save paths must not diverge on validation, or a
    // half-authored gate gets through whichever one is missing it.
    const nameError = validateQuestionNames();
    if (nameError) {
      setInputError([true, nameError]);
      setSaveCurrent(false);
      return;
    }

    const stepError = validateApplicabilitySteps();
    if (stepError) {
      setInputError([true, stepError]);
      setSaveCurrent(false);
      return;
    }

    // Validate QA weighting totals 100 for yes/no scoring
    if (yesNoScoring && hasQualityQuestions()) {
      const total = calculateTotalQaWeighting();
      if (Math.abs(total - 100) > 0.01) {  // Allow small floating point differences
        setInputError([true, `QA Weighting must total exactly 100%. Current total: ${total.toFixed(2)}%`]);
        setSaveCurrent(false);
        return;
      }

      // Validate Compliance weighting totals 100 for yes/no scoring - same rule as handleSave
      const complianceTotal = calculateTotalComplianceWeighting();
      if (Math.abs(complianceTotal - 100) > 0.01) {  // Allow small floating point differences
        setInputError([true, `Compliance Weighting must total exactly 100%. Current total: ${complianceTotal.toFixed(2)}%`]);
        setSaveCurrent(false);
        return;
      }
    }

    if (name !== templateName) {
      setInputError([true, `Your scorecard name does not match the question set you are editing.
        Please enter the correct scorecard name.`])
      return;
    }

    setSaving(true);
    try {
      const payload = {
            currentTemplateName: templateName,
            campaign: campaign || undefined,
            language: selectedLanguage,
            scoringSystem,
            questions: withSortOrder(questions),
          };
        devLog("PAYLOAD Current: ", payload)
      await createTemplateApi(payload);

      // Upload knowledge base files for this template and register in KB management
      if (upload.length !== 0) {
        for (let i = 0; i < upload.length; i++) {
          const filename = upload[i].name;
          const s3Up = await presign(filename, 'scorecard-kb', templateName);
          await axios.put(s3Up.url, upload[i]);
          // Register the file in KB management system linked to this template
          await addKbFileToTemplate(templateName, filename, s3Up.key || filename);
        }
        setUpload([]);
      }

      setAlert?.({ variant: 'success', heading: 'Scorecard Updated', text: 'Scorecard updated successfully.' });
      setDirty(false);
      navigate('/templates');
    } catch (err) {
      console.error('Save failed', err);
      setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to save scorecard.' });
    } finally {
      setSaving(false);
      setSaveCurrent(false);
    }
  };

  if (loading) {
    return <LoadingState variant="form" label="Loading scorecard" />;
  }


  return (
    <>
    <div className="space-y-6">

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{name ? 'Edit Scorecard' : 'New Scorecard'}</h1>
          <p className="text-gray-500 text-sm mt-1">Define the questions and weightings this scorecard assesses.</p>
        </div>
      </div>
      <div className="card space-y-4">
        <h2 className="text-sm font-bold text-gray-900">Scorecard Details</h2>
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-1.5">Scorecard Name</label>
          <input
          className={`input-field ${inputError[0] ? "input-error" : ""}`}
          autoFocus={inputError[0]}
          value={templateName}
          onChange={e=>{setTemplateName(e.target.value); setDirty(true);}} />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-1.5">Campaign (optional)</label>
          <SelectField value={campaign} onChange={e=>{setCampaign(e.target.value); setDirty(true);}} className="select-field">
            <option value="">None</option>
            {campaigns.map(c => (
              <option key={c.campaignName} value={c.campaignName}>{c.campaignName}</option>
            ))}
          </SelectField>
        </div>

        <div className="border-t border-gray-100 pt-4">
          <label className="block text-sm font-semibold text-gray-800 mb-1.5">Language</label>
          <div className="flex flex-wrap gap-2">
            {LANGUAGE_OPTIONS.map(opt => (
              <label
                key={opt.value}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition-colors text-sm ${
                  selectedLanguage === opt.value
                    ? 'border-cloudza-300 bg-cloudza-50 text-cloudza-800'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="scorecard-language"
                  checked={selectedLanguage === opt.value}
                  onChange={() => {setSelectedLanguage(opt.value); setDirty(true);}}
                  className="sr-only"
                />
                {selectedLanguage === opt.value && <FaCheck className="text-xs" aria-hidden="true" />}
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Scoring Type Toggle */}
        <div className="border-t border-gray-100 pt-4">
          <label className="block text-sm font-semibold text-gray-800 mb-1.5">Scoring System</label>
          <div className="space-y-2">
            {SCORING_SYSTEM_OPTIONS.map(option => (
              <label
                key={option.value}
                className={`flex items-start gap-3 border rounded-xl p-3 cursor-pointer transition-colors ${
                  scoringSystem === option.value
                    ? 'border-cloudza-300 bg-cloudza-50 text-cloudza-800'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="scoring-system"
                  checked={scoringSystem === option.value}
                  onChange={() => chooseScoringSystem(option.value)}
                  className="mt-0.5"
                />
                <span>
                  <span className="block text-sm font-semibold">{option.label}</span>
                  <span className="block text-xs text-gray-500 mt-0.5">{option.description}</span>
                </span>
              </label>
            ))}
          </div>
          <div className="mt-2 p-3 bg-cloudza-50 border border-cloudza-100 rounded-xl text-sm">
            <strong>Current:</strong> {complianceOnly ? (
              <span>Compliance-only: every question is a pass/fail compliance check, with no QA score</span>
            ) : yesNoScoring ? (
              <span>Yes/No scoring with Quality and Compliance weighting</span>
            ) : (
              <span>Numeric scoring (0-100) with single weight value</span>
            )}
          </div>
        </div>
        <div className="border-t border-gray-100 pt-4 space-y-6">
          <h2 className="text-sm font-bold text-gray-900">Questions</h2>
          {questions.map((q, idx) => (
            <div key={q.clientKey} className="border border-gray-200 rounded-lg p-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-2.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-cloudza-50 text-cloudza-700 text-xs font-bold">{idx + 1}</span>
                  <span className="text-sm font-semibold text-gray-800">
                    {(q.questionName && String(q.questionName).trim()) || `Question ${idx + 1}`}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold ${TYPE_BADGE_CLASSES[q.type] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                    {ASSESSMENT_TYPE_SHORT_LABELS[q.type] || q.type}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => moveQuestion(idx, -1)}
                    disabled={idx === 0}
                    aria-label={`Move question ${idx + 1} up`}
                    title="Move up"
                    className="btn-outline !py-1 !px-2 !text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <FaArrowUp />
                  </button>
                  <button
                    onClick={() => moveQuestion(idx, 1)}
                    disabled={idx === questions.length - 1}
                    aria-label={`Move question ${idx + 1} down`}
                    title="Move down"
                    className="btn-outline !py-1 !px-2 !text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <FaArrowDown />
                  </button>
                  <button
                    onClick={() => removeQuestion(idx)}
                    className="btn-outline !py-1 !px-2.5 !text-xs !text-red-600 !border-red-200 hover:!bg-red-50"
                  >
                    <FaTrash /> Remove
                  </button>
                </div>
              </div>

              {/* Row 1: Question Name, Section, Type */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Question Name</label>
                  <input
                    value={q.questionName}
                    onChange={e=>updateQuestion(idx,'questionName',e.target.value)}
                    className="input-field"
                    placeholder="e.g., greeting_check"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Section</label>
                  <input
                    type="text"
                    value={q.section || ''}
                    onChange={e=>updateQuestion(idx,'section',e.target.value)}
                    className="input-field"
                    placeholder="e.g., Introduction, Pitch, Close"
                  />
                  <p className="text-xs text-gray-500 mt-1">Enter custom section name</p>
                </div>                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Type</label>
                  {complianceOnly ? (
                    <>
                      <p className="input-field bg-gray-50 text-gray-700">Compliance</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Compliance-only scorecard: every question is a pass/fail compliance check
                      </p>
                    </>
                  ) : (
                    <>
                      <SelectField
                        value={q.type}
                        onChange={e=>updateQuestion(idx,'type',e.target.value)}
                        className="select-field"
                      >
                        {ASSESSMENT_TYPE_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </SelectField>
                      <p className="text-xs text-gray-500 mt-1">
                        Quality: scored | Compliance &amp; Conduct: pass/fail, reported separately
                      </p>
                    </>
                  )}
                </div>
              </div>

              {/* Row 2: Assessment Question */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Assessment Question</label>
                <textarea
                  rows="2"
                  value={q.assessment || q.description}
                  onChange={e=>{
                    updateQuestion(idx,'assessment',e.target.value);
                    updateQuestion(idx,'description',e.target.value);
                  }}
                  className="input-field w-full"
                  placeholder="Did the agent greet the customer correctly?"
                />
              </div>

              {/* Row 3: Expected Criteria */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Expected Criteria</label>
                <textarea
                  rows="2"
                  value={q.expected || ''}
                  onChange={e=>updateQuestion(idx,'expected',e.target.value)}
                  className="input-field w-full"
                  placeholder="Agent should greet within 10 seconds..."
                />
              </div>

              {/* Row 4: Weighting (conditional based on scoring type) */}
              {yesNoScoring ? (
                // Yes/No Scoring Fields
                <div className={weightingGridClass(q.type)}>
                  {/* Only Quality questions carry weighting */}
                  {isWeightedType(q.type) && (
                    <>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">QA Weighting (%)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={q.qaWeighting || q.weight || ''}
                          onChange={e=>updateQuestion(idx,'qaWeighting',e.target.value)}
                          onWheel={e => { e.target.blur(); e.stopPropagation(); setTimeout(() => e.target.focus(), 0); }}
                          className="input-field"
                          placeholder="2.5"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Compliance Weighting (%)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={q.complianceWeighting || ''}
                          onChange={e=>updateQuestion(idx,'complianceWeighting',e.target.value)}
                          onWheel={e => { e.target.blur(); e.stopPropagation(); setTimeout(() => e.target.focus(), 0); }}
                          className="input-field"
                          placeholder="5.56"
                        />
                      </div>
                    </>
                  )}
                  <div>
                    <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 mb-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={q.isCriticalFail || false}
                        onChange={e=>updateQuestion(idx,'isCriticalFail',e.target.checked)}
                      />
                      <span>Critical Fail</span>
                    </label>
                    <div className="text-xs text-gray-500 mt-1">
                      {q.isCriticalFail ? (
                        <span className="inline-flex items-center gap-1 text-amber-700">
                          <FaExclamationTriangle className="text-[10px]" aria-hidden="true" />
                          Auto-fails call if "{q.success === 'no' ? 'Yes' : 'No'}"
                        </span>
                      ) : 'Not critical'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Success Answer
                    </label>
                    <SelectField
                      value={q.success || 'yes'}
                      onChange={e => updateQuestion(idx, 'success', e.target.value)}
                      className="input-field"
                    >
                      <option value="yes">Yes awards points</option>
                      <option value="no">No awards points</option>
                    </SelectField>
                    <div className="text-xs text-gray-500 mt-1">
                      <span className="inline-flex items-center gap-1">
                        <FaCheck className="text-[10px] text-green-600" aria-hidden="true" />
                        {q.success === 'no' ? '"No" = Success' : '"Yes" = Success'}
                      </span>
                    </div>
                  </div>
                  {q.type === 'Compliance' && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        Priority
                      </label>
                      <SelectField
                        value={q.priority || 'medium'}
                        onChange={e => updateQuestion(idx, 'priority', e.target.value)}
                        className="input-field"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </SelectField>
                    </div>
                  )}
                </div>
              ) : (
                // Legacy Scored Fields
                <div className={`grid grid-cols-1 ${q.type === 'Compliance' ? 'md:grid-cols-2 gap-4' : ''}`}>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Weight (0-100)</label>
                    <input
                      type="number"
                      value={q.weight || ''}
                      onChange={e=>updateQuestion(idx,'weight',e.target.value)}
                      className="input-field"
                      placeholder="Weight for scored questions"
                    />
                    <p className="text-xs text-gray-500 mt-1">Question will be scored 0-100, then weighted by this value</p>
                  </div>
                  {q.type === 'Compliance' && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        Priority
                      </label>
                      <SelectField
                        value={q.priority || 'medium'}
                        onChange={e => updateQuestion(idx, 'priority', e.target.value)}
                        className="input-field"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </SelectField>
                    </div>
                  )}
                </div>
              )}

              {/* Row 5: Applicability pre-checks (Agent Behavior and Conduct only) */}
              {supportsApplicability(q.type) && (
                <div className="border-t border-gray-100 pt-3">
                  <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 mb-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={q.isConditional || false}
                      onChange={e=>{
                        updateQuestion(idx,'isConditional',e.target.checked);
                        // Start them off with one empty step so the control is obvious.
                        if (e.target.checked && (q.applicabilityConditions || []).length === 0) {
                          addStep(idx);
                        }
                      }}
                    />
                    <span>Not always applicable</span>
                  </label>

                  {q.isConditional ? (
                    <>
                      <div className="text-xs text-gray-500 mt-1 mb-2 space-y-1">
                        <p>
                          Write each step as a <strong>condition</strong> — something simply true or
                          false of the call. Not an instruction: no "mark Yes", no "stop", no
                          "do not proceed".
                        </p>
                        <p>
                          <strong>Yes</strong> → the pre-check passes and the question is assessed,
                          and can pass or fail normally.{' '}
                          <strong>No</strong> → the pre-check fails, so the question{' '}
                          <strong>did not apply</strong> to this call. Recorded as Not Applicable:
                          not a pass, not a failure, and it cannot fail the call.
                        </p>
                        <p>Every step must be Yes for the question to be assessed.</p>
                      </div>

                      <div className="space-y-2">
                        {(q.applicabilityConditions || []).map((step, sIdx) => (
                          <div key={sIdx} className="border border-gray-100 rounded-lg p-3 space-y-2">
                            <div className="flex items-center justify-between">
                              <label className="block text-xs font-medium text-gray-600">
                                Step {sIdx + 1}
                              </label>
                              <button
                                type="button"
                                onClick={() => removeStep(idx, sIdx)}
                                className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                aria-label={`Remove step ${sIdx + 1}`}
                                title="Remove step"
                              >
                                <FaTimes />
                              </button>
                            </div>
                            <textarea
                              rows="2"
                              value={step}
                              onChange={e=>updateStep(idx, sIdx, e.target.value)}
                              className="input-field w-full"
                              placeholder="e.g. An NRV bundle is referred to anywhere in the transcript"
                            />
                            {looksLikeScoringInstruction(step) && (
                              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                                <FaExclamationTriangle className="inline text-[10px] mr-1" aria-hidden="true" />
                                This reads like a scoring instruction. A pre-check step is a{' '}
                                <strong>condition</strong> that is either true or false of the call —
                                for example "An NRV bundle was offered to the customer". The
                                pre-check never marks or scores anything; if the condition holds,
                                the question is then assessed normally.
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      <button
                        type="button"
                        onClick={() => addStep(idx)}
                        className="btn-outline mt-2"
                      >
                        <FaPlus /> Add step
                      </button>

                      {!(q.applicabilityConditions || []).some(s => String(s).trim()) && (
                        <div className="text-xs text-red-600 mt-2">
                          <FaExclamationTriangle className="inline text-[10px] mr-1" aria-hidden="true" />
                          Add at least one step, or untick the box — otherwise this question is
                          assessed on every call.
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-xs text-gray-500 mt-1">Assessed on every call.</div>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* No Quality questions yet — weighting doesn't apply, nothing to total */}
          {yesNoScoring && !complianceOnly && !hasQualityQuestions() && (
            <div className="p-4 rounded-lg mb-4 bg-gray-50 border border-gray-200">
              <p className="text-xs text-gray-500">
                No Quality questions on this scorecard yet — QA/Compliance weighting only applies to
                Quality-type questions (Compliance questions are pass/fail and are not weighted), so there's
                nothing to total here. Save is not blocked by this.
              </p>
            </div>
          )}

          {/* QA Weighting Total Indicator */}
          {yesNoScoring && hasQualityQuestions() && (
            <div className={`p-4 rounded-lg mb-4 ${
              Math.abs(calculateTotalQaWeighting() - 100) < 0.01
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  Total QA Weighting:
                </span>
                <span className={`text-lg font-bold ${
                  Math.abs(calculateTotalQaWeighting() - 100) < 0.01
                    ? 'text-green-700'
                    : 'text-red-700'
                }`}>
                  {calculateTotalQaWeighting().toFixed(2)}%
                </span>
              </div>
              {Math.abs(calculateTotalQaWeighting() - 100) < 0.01 ? (
                <p className="text-xs text-green-600 mt-1">
                  <FaCheck className="inline text-[10px] mr-1" aria-hidden="true" />
                  Total is exactly 100%
                </p>
              ) : (
                <p className="text-xs text-red-600 mt-1">
                  <FaExclamationTriangle className="inline text-[10px] mr-1" aria-hidden="true" />
                  Must equal 100% to save (currently {(calculateTotalQaWeighting() - 100).toFixed(2)}% {calculateTotalQaWeighting() > 100 ? 'over' : 'under'})
                </p>
              )}
            </div>
          )}

          {/* Compliance Weighting Total Indicator */}
          {yesNoScoring && hasQualityQuestions() && (
            <div className={`p-4 rounded-lg mb-4 ${
              Math.abs(calculateTotalComplianceWeighting() - 100) < 0.01
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  Total Compliance Weighting:
                </span>
                <span className={`text-lg font-bold ${
                  Math.abs(calculateTotalComplianceWeighting() - 100) < 0.01
                    ? 'text-green-700'
                    : 'text-red-700'
                }`}>
                  {calculateTotalComplianceWeighting().toFixed(2)}%
                </span>
              </div>
              {Math.abs(calculateTotalComplianceWeighting() - 100) < 0.01 ? (
                <p className="text-xs text-green-600 mt-1">
                  <FaCheck className="inline text-[10px] mr-1" aria-hidden="true" />
                  Total is exactly 100%
                </p>
              ) : (
                <p className="text-xs text-red-600 mt-1">
                  <FaExclamationTriangle className="inline text-[10px] mr-1" aria-hidden="true" />
                  Must equal 100% to save (currently {(calculateTotalComplianceWeighting() - 100).toFixed(2)}% {calculateTotalComplianceWeighting() > 100 ? 'over' : 'under'})
                </p>
              )}
            </div>
          )}

          <button onClick={addQuestion} className="btn-outline"><FaPlus /> Add Question</button>
        </div>
        <div className="mt-4 border-t border-gray-100 pt-4">
          <h2 className="text-sm font-bold text-gray-900 mb-1.5">
            Knowledge Base Files
          </h2>
          {/* Display attached KB files */}
          {name && kbFiles.length > 0 && (
            <div className="mb-4">
              <p className="text-xs text-gray-500 mb-2">Attached files:</p>
              <ul className="space-y-2">
                {kbFiles.map((fileId) => (
                  <li key={fileId} className="flex items-center justify-between bg-gray-50 p-2 rounded-lg border border-gray-200">
                    <span className="text-sm text-gray-700">{fileId}</span>
                    <button
                      type="button"
                      onClick={() => setDeleteKbInfo({ open: true, fileId })}
                      className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      aria-label={`Delete file ${fileId}`}
                      title="Delete file"
                    >
                      <FaTimes />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {name && kbFiles.length === 0 && (
            <p className="text-xs text-gray-500 mb-2">No knowledge base files attached.</p>
          )}
          {/* Upload new files */}
          <p className="text-xs text-gray-500 mb-2">Upload new files:</p>
          <FileUpload onChange={handleFileAdd} value={upload} multiple />
        </div>
        {inputError[0] && (
          <div className="custom-alert" ref={errorRef}>
            <span>{inputError[1]}</span>
            <button className="dismiss-btn" onClick={() => setInputError([false])}>
              <FaTimes />
            </button>
          </div>
        )}         
        <div className="border-t border-gray-100 pt-4 flex flex-wrap gap-2 justify-end">
          {name && (
            <button
              onClick={() => setSaveCurrent(true)}
              disabled={saving}
              className="btn-outline !text-amber-700 !border-amber-300 hover:!bg-amber-50"
            >
              <><FaPen /> {saving ? 'Saving...' : 'Overwrite Current Version'}</>
            </button>
          )}
          <button onClick={() => setSaveConfirm(true)} disabled={saving} className="btn-primary">
            <><FaSave /> {saving ? 'Saving...' : 'Save New Version'}</>
          </button>
        </div>

      </div>
    </div>
    <ConfirmationModal
      open={saveConfirm}
      title="Save Confirmation"
      message={`Are you sure you want to save the changes to ${templateName || 'this scorecard'}?`}
      confirmText="Confirm Save"
      onClose={() => setSaveConfirm(false)}
      onConfirm={handleSave}
    />
    <ConfirmationModal
      open={saveCurrent}
      title="Save Confirmation"
      message={`Are you sure you want to save the changes to the current scorecard ${name || ''}?`}
      confirmText="Confirm Save"
      onClose={() => setSaveCurrent(false)}
      onConfirm={handleSaveCurrent}
    />
    <ConfirmationModal
      open={confirmComplianceOnly}
      title="Switch to Compliance-only"
      message={`${nonComplianceCount} question${nonComplianceCount === 1 ? '' : 's'} will become Compliance questions and lose any QA weighting. A compliance-only scorecard has no QA score.`}
      confirmText="Convert to Compliance"
      onClose={() => setConfirmComplianceOnly(false)}
      onConfirm={() => {
        setConfirmComplianceOnly(false);
        applyScoringSystem(SCORING_SYSTEM.COMPLIANCE);
      }}
    />
    <DeleteConfirmationModal
      open={deleteKbInfo.open}
      itemName={deleteKbInfo.fileId || ''}
      onClose={() => setDeleteKbInfo({ open: false, fileId: null })}
      onConfirm={handleDeleteKbFile}
    />
    <ConfirmationModal
      open={blocker.state === 'blocked'}
      title="Unsaved Changes"
      message="You have unsaved changes. Are you sure you want to leave?"
      confirmText="Leave"
      onClose={() => blocker.reset()}
      onConfirm={() => blocker.proceed()}
    />
    </>
  );
};

export default TemplateEditor;
